/**
 * The climactic wave (streak and win): a crescendo of pops along the chain
 * with a ritardando inside a fixed time budget, a beat of anticipation (the final node dips), then
 * a bigger, longer release with a soft settle. The shape is captured from
 * MOTION when the wave is planned, so a running wave never changes mid-way.
 */
import { cubicBezier, waveEase } from "./easing";
import { MOTION, type WaveEasingName } from "./motionConfig";

export interface Wave {
  nodes: string[];
  /** ms after the plan starts before the first pop. */
  delay: number;
  /** Start of each node's pop, relative to delay. */
  starts: number[];
  /** Peak scale of each non-final pop. */
  peaks: number[];
  popMs: number;
  settle: number;
  easing: WaveEasingName;
  pause: number;
  dip: number;
  finalPeak: number;
  finalMs: number;
  finalRise: number;
  finalHold: number;
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp01 = (t: number) => Math.max(0, Math.min(1, t));
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
const easeInOut = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

const EASINGS: Record<WaveEasingName, (t: number) => number> = {
  prototype: waveEase,
  easeOut: cubicBezier(0.33, 1, 0.68, 1),
  easeInOut: cubicBezier(0.65, 0, 0.35, 1),
  linear: (t) => t,
};

export interface WaveScheduleParams {
  /** Total of the n-1 build-up gaps (ms), whatever the chain length. */
  buildMs: number;
  /** Ratio of the last gap to the first (1 = even; >1 slows down). */
  ritardando: number;
  /** Per-gap clamp (ms); when hit, the total may miss buildMs. */
  minGap: number;
  maxGap: number;
  /** Anticipation beat before the final node; never compressed. */
  pause: number;
  crescendoFrom: number;
  crescendoTo: number;
}

export interface WaveSchedule {
  /** Start of each node's pop, ms from the first pop. */
  delays: number[];
  /** Peak scale of each non-final pop (ramping first to second-to-last). */
  peaks: number[];
}

/**
 * The n-1 build-up gaps: geometric growth from first to last by the
 * ritardando ratio (gap j = g0 * r^(j/(m-1))), scaled so they sum to
 * buildMs, then each clamped to [minGap, maxGap].
 */
export function waveGaps(
  n: number,
  p: Pick<WaveScheduleParams, "buildMs" | "ritardando" | "minGap" | "maxGap">,
): number[] {
  const m = n - 1;
  if (m < 1) return [];
  const r = Math.max(1e-6, p.ritardando);
  const w = Array.from({ length: m }, (_, j) =>
    m === 1 ? 1 : Math.pow(r, j / (m - 1)),
  );
  const sum = w.reduce((a, b) => a + b, 0);
  const lo = Math.min(p.minGap, p.maxGap);
  const hi = Math.max(p.minGap, p.maxGap);
  return w.map((x) => Math.max(lo, Math.min(hi, (p.buildMs * x) / sum)));
}

/**
 * Pop times and peaks for a wave over n nodes: build-up gaps sized by the
 * budget (see waveGaps), then the anticipation pause before the final node.
 * Total ~ buildMs + pause + release, whatever the path length.
 */
export function waveSchedule(n: number, p: WaveScheduleParams): WaveSchedule {
  const gaps = waveGaps(n, p);
  const delays = [0];
  gaps.forEach((g, j) =>
    delays.push(delays[j] + g + (j === gaps.length - 1 ? p.pause : 0)),
  );
  const pre = Math.max(1, n - 1);
  const peaks = Array.from({ length: pre }, (_, i) =>
    lerp(p.crescendoFrom, p.crescendoTo, pre === 1 ? 1 : i / (pre - 1)),
  );
  return { delays, peaks };
}

/** A wave over `nodes`, shaped from the current MOTION values. */
export function buildWave(
  nodes: string[],
  kind: "streak" | "win",
  delay: number,
): Wave {
  const { delays, peaks } = waveSchedule(nodes.length, {
    buildMs: kind === "win" ? MOTION.winBuildMs : MOTION.waveBuildMs,
    ritardando: MOTION.waveRitardando,
    minGap: MOTION.waveGapMin,
    maxGap: MOTION.waveGapMax,
    pause: MOTION.wavePause,
    crescendoFrom: MOTION.waveCrescendoFrom,
    crescendoTo: MOTION.waveCrescendoTo,
  });
  return {
    nodes,
    delay,
    starts: delays,
    peaks,
    popMs: MOTION.waveMs,
    settle: MOTION.waveSettle,
    easing: MOTION.waveEasing,
    pause: MOTION.wavePause,
    dip: MOTION.waveDip,
    finalPeak: kind === "win" ? MOTION.winFinalPeak : MOTION.waveFinalPeak,
    finalMs: MOTION.waveFinalMs,
    finalRise: MOTION.waveFinalRise,
    finalHold: MOTION.waveFinalHold,
  };
}

/** ms from the plan start until the wave is over. */
export function waveEnd(w: Wave): number {
  return w.delay + w.starts[w.starts.length - 1] + w.finalMs;
}

/** When (ms after the plan start) the final node is released. */
export function releaseAt(w: Wave): number {
  return w.delay + w.starts[w.starts.length - 1];
}

/**
 * A non-final pop: keyframes 1 -> peak (40%) -> settle (75%) -> 1, linear
 * between keyframes, the chosen easing on the overall progress.
 */
export function waveCurve(
  t: number,
  peak: number,
  easing: WaveEasingName = "prototype",
  settle = 1,
): number {
  const p = EASINGS[easing](t);
  const kf: [number, number][] = [
    [0, 1],
    [0.4, peak],
    [0.75, settle],
    [1, 1],
  ];
  let i = 1;
  while (i < kf.length - 1 && p > kf[i][0]) i++;
  const [o0, v0] = kf[i - 1];
  const [o1, v1] = kf[i];
  return v0 + ((v1 - v0) * (p - o0)) / (o1 - o0);
}

/** The release: from the dip up to the peak, a held beat, a soft settle. */
export function releaseCurve(t: number, w: Wave): number {
  const from = w.pause > 0 ? w.dip : 1;
  const held = w.finalPeak - (w.finalPeak - 1) * 0.06;
  if (t <= w.finalRise)
    return lerp(from, w.finalPeak, easeOut(t / w.finalRise));
  if (t <= w.finalHold) {
    return lerp(
      w.finalPeak,
      held,
      (t - w.finalRise) / (w.finalHold - w.finalRise),
    );
  }
  return lerp(
    held,
    1,
    easeInOut(clamp01((t - w.finalHold) / (1 - w.finalHold))),
  );
}

export function waveScale(w: Wave, id: string, el: number): number {
  const i = w.nodes.lastIndexOf(id);
  if (i < 0) return 1;
  const local = el - w.delay - w.starts[i];
  if (i === w.nodes.length - 1) {
    if (local < 0) {
      if (w.pause <= 0 || local < -w.pause) return 1;
      return lerp(1, w.dip, easeInOut((local + w.pause) / w.pause));
    }
    return local >= w.finalMs ? 1 : releaseCurve(local / w.finalMs, w);
  }
  const t = local / w.popMs;
  return t <= 0 || t >= 1 ? 1 : waveCurve(t, w.peaks[i], w.easing, w.settle);
}
